import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { PRODUCTS, PRODUCT_CATEGORIES } from "@/content/site";
import { productPath } from "@/content/productSeo";
import { Reveal } from "@/lib/motion";
import { Seo, ORIGIN } from "@/components/Seo";
import { useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

const ctaLabel = (cta, t) => {
  if (cta === "buy") return t(UI.cta.buy);
  if (cta === "download") return t(UI.cta.download);
  return t(UI.cta.view);
};

// JSON-LD: list every book title with author + cover so Google understands them.
const buildBooksJsonLd = () => {
  const author = { "@type": "Person", name: "Florian Teodorescu" };
  const publisher = { "@type": "Organization", name: "WERKVERDUNG" };
  const items = [];
  let pos = 1;
  PRODUCTS.forEach((p) => {
    if (["coloring", "poetry", "novels"].includes(p.category) && p.volumes) {
      p.volumes.forEach((v) => {
        items.push({
          "@type": "ListItem",
          position: pos++,
          item: {
            "@type": "Book",
            name: v.label,
            author,
            publisher,
            url: v.link,
            image: v.cover || p.image,
            isPartOf: p.title,
          },
        });
      });
    }
  });
  return { "@context": "https://schema.org", "@type": "ItemList", name: "WERKVERDUNG — Bücher", itemListElement: items };
};

const BOOKS_JSONLD = buildBooksJsonLd();

export default function Products({ locale = "de" }) {
  const { t } = useLang();
  useLocaleRoute(locale, localizePath("/products", locale === "de" ? "en" : "de"));
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === active);

  return (
    <div data-testid="products-page">
      <Seo
        title={UI.seo.products.title[locale]}
        description={UI.seo.products.desc[locale]}
        path={localizePath("/products", locale)}
        lang={locale}
        image={`${ORIGIN}/og-products.jpg`}
        alternates={coreAlternates("/products")}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BOOKS_JSONLD)}</script>
      </Helmet>
      <section className="pt-36 md:pt-44 pb-16 border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {t(UI.products.title)}</span>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-ink leading-[0.98] max-w-4xl">
              {t(UI.products.intro)}
            </h1>
            <p className="mt-6 font-mono text-[12px] text-stone/70">{t(UI.products.note)}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="product-filters">
            {PRODUCT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`product-filter-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-5 py-2 text-[13px] font-medium transition-all duration-300 ${
                  active === c.id
                    ? "bg-bronze text-paper"
                    : "border border-border text-stone hover:border-ink hover:text-ink"
                }`}
              >
                {t(c.label)}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1], delay: (i % 3) * 0.06 }}
                >
                  <div data-testid={`product-${p.id}`} className="group flex flex-col h-full">
                    <Link to={productPath(p.id, locale)} data-testid={`product-detail-link-${p.id}`} className="block">
                      <div className="relative overflow-hidden bg-secondary aspect-[4/5] mb-5">
                        <img
                          src={p.image}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                        />
                        <span className="absolute top-4 left-4 rounded-full bg-paper/90 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ink">
                          {p.platform}
                        </span>
                      </div>
                    </Link>
                    <Link to={productPath(p.id, locale)} className="block">
                      <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight hover:text-bronze transition-colors">{p.title}</h3>
                    </Link>
                    <p className="mt-2 text-sm text-stone flex-1">{t(p.desc)}</p>
                    <Link
                      to={productPath(p.id, locale)}
                      data-testid={`product-more-${p.id}`}
                      className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-bronze hover:text-ink transition-colors self-start"
                    >
                      {locale === "de" ? "Details ansehen" : "View details"} <ArrowUpRight size={13} />
                    </Link>
                    {p.volumes ? (
                      <ul data-testid={`product-volumes-${p.id}`} className="mt-5 self-stretch space-y-2.5">
                        {p.volumes.map((v, vi) => (
                          <li key={vi}>
                            <a
                              href={v.link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`product-vol-${p.id}-${vi}`}
                              className="group/vol flex items-center justify-between gap-3 border-b border-border pb-2 text-sm text-ink/80 hover:text-bronze transition-colors duration-300"
                            >
                              <span className="flex items-center gap-3">
                                {v.cover && (
                                  <img src={v.cover} alt={v.label} loading="lazy" className="h-10 w-8 shrink-0 rounded-[3px] object-cover" />
                                )}
                                <span>{v.label}</span>
                              </span>
                              <ArrowUpRight size={14} className="shrink-0 transition-transform duration-300 group-hover/vol:translate-x-0.5 group-hover/vol:-translate-y-0.5" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : p.link ? (
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`product-cta-${p.id}`}
                        className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-bronze text-paper px-6 py-3 text-[13px] font-semibold hover:bg-[#f0be6e] transition-colors duration-300"
                      >
                        {ctaLabel(p.cta, t)} <ArrowUpRight size={14} />
                      </a>
                    ) : (
                      <span className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-border px-6 py-3 text-[13px] font-medium text-ink/70">
                        {t(UI.cta.liveSoon)}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
